import SEO from '../components/SEO'
import StageLayout from '../components/StageLayout'

export interface CompareRow {
  feature: string
  TokenDance: string
  competitor: string
}

export interface CompareData {
  slug: string
  competitor: string
  competitorDomain: string
  tagline: string
  summary: string
  rows: CompareRow[]
  chooseUs: string[]
  chooseThem: string[]
  fullMarkdownPath: string
}

interface Props {
  data: CompareData
}

export default function Compare({ data }: Props) {
  const title = `TokenDance vs ${data.competitor} — FDE and LLM infrastructure`
  const description = data.summary
  const path = `/vs/${data.slug}`

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: `TokenDance vs ${data.competitor}`,
      description,
      author: { '@type': 'Organization', name: 'TokenDance' },
      publisher: { '@id': 'https://tokendance.net/#organization' },
      mainEntityOfPage: `https://tokendance.net${path}`,
      datePublished: '2026-04-24',
      dateModified: '2026-04-24',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: `Is TokenDance a good ${data.competitor} alternative?`,
          acceptedAnswer: {
            '@type': 'Answer',
            text: `TokenDance is different from ${data.competitor}: it combines forward-deployed engineering with LLM infrastructure for teams shipping production AI systems. ${data.summary}`,
          },
        },
      ],
    },
  ]

  return (
    <>
      <SEO title={title} description={description} path={path} jsonLd={jsonLd} />
      <StageLayout active="why">
        <div className="v2-center v2-compare-block">
          <h1 className="v2-compare-headline">
            TokenDance <span className="v2-compare-vs">vs</span> {data.competitor}
          </h1>
          <p className="v2-compare-tagline">{data.tagline}</p>
          <p className="v2-compare-summary">{data.summary}</p>

          <table className="v2-compare-table">
            <thead>
              <tr>
                <th scope="col">Feature</th>
                <th scope="col">TokenDance</th>
                <th scope="col">{data.competitor}</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row) => (
                <tr key={row.feature}>
                  <th scope="row">{row.feature}</th>
                  <td>{row.TokenDance}</td>
                  <td>{row.competitor}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="v2-compare-picks">
            <section className="v2-compare-pick">
              <h2 className="v2-compare-pick-heading">Choose TokenDance if</h2>
              <ul>
                {data.chooseUs.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </section>
            <section className="v2-compare-pick">
              <h2 className="v2-compare-pick-heading">Choose {data.competitor} if</h2>
              <ul>
                {data.chooseThem.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </section>
          </div>

          <p className="v2-compare-md-link">
            Full comparison (machine-readable):{' '}
            <a href={data.fullMarkdownPath}>{data.fullMarkdownPath}</a>
            <span className="v2-compare-divider" aria-hidden="true">·</span>
            <time dateTime="2026-04-24">Last updated April 24, 2026</time>
          </p>
        </div>
      </StageLayout>
    </>
  )
}
